import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { EMPLOYEE_RANGES, PROVINCES, SERVICE_OPTIONS, SITE } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  first_name: "",
  last_name: "",
  business_name: "",
  email: "",
  phone: "",
  province: "",
  software: "",
  employees: "",
  services: [],
  message: "",
  consent: false,
  website: "",
};

const inputCls = (err) =>
  `h-12 w-full rounded-xl border bg-paper px-4 text-[15px] outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:bg-white ${
    err ? "border-red-400" : "border-ink/10"
  }`;

const labelCls = "mb-1.5 block text-sm font-semibold text-ink";
const errorCls = "mt-1.5 text-xs text-red-600";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleService = (service) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(service)
        ? f.services.filter((s) => s !== service)
        : [...f.services, service],
    }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "First name is required.";
    if (!form.last_name.trim()) e.last_name = "Last name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
    if (!form.services.length) e.services = "Select at least one service.";
    if (!form.consent) e.consent = "Please confirm you're happy to be contacted.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus("sending");
    setServerError("");
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setServerError(err.message || "Something went wrong. Please try again or call us.");
    }
  };

  if (status === "success") {
    return (
      <div
        className="rounded-3xl border border-ink/5 bg-white p-10 text-center shadow-subtle"
        data-testid="contact-success-panel"
        role="status"
      >
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
        </span>
        <h2 className="mt-5 font-cabinet text-2xl font-bold tracking-tight">
          Thank you{form.first_name ? `, ${form.first_name}` : ""}.
        </h2>
        <p className="mx-auto mt-3 max-w-md leading-relaxed text-slate-600">
          Your inquiry is on its way. We&apos;ll connect you with the right member of the
          Postings team.
        </p>
        <button
          onClick={() => {
            setForm(EMPTY);
            setStatus("idle");
          }}
          data-testid="contact-send-another"
          className="mt-8 inline-flex h-11 items-center rounded-full border border-ink/15 px-6 text-sm font-bold text-ink transition-colors hover:border-brand hover:text-brand"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="rounded-3xl border border-ink/5 bg-white p-8 shadow-subtle sm:p-10"
      data-testid="contact-form"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="first_name" className={labelCls}>First Name *</label>
          <input
            id="first_name"
            data-testid="contact-input-first-name"
            value={form.first_name}
            onChange={(e) => set("first_name", e.target.value)}
            autoComplete="given-name"
            className={inputCls(errors.first_name)}
            placeholder="Jane"
          />
          {errors.first_name && <p className={errorCls} role="alert">{errors.first_name}</p>}
        </div>
        <div>
          <label htmlFor="last_name" className={labelCls}>Last Name *</label>
          <input
            id="last_name"
            data-testid="contact-input-last-name"
            value={form.last_name}
            onChange={(e) => set("last_name", e.target.value)}
            autoComplete="family-name"
            className={inputCls(errors.last_name)}
            placeholder="Douglas"
          />
          {errors.last_name && <p className={errorCls} role="alert">{errors.last_name}</p>}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="business_name" className={labelCls}>Business Name</label>
          <input
            id="business_name"
            data-testid="contact-input-business-name"
            value={form.business_name}
            onChange={(e) => set("business_name", e.target.value)}
            autoComplete="organization"
            className={inputCls()}
            placeholder="Your business name"
          />
        </div>
        <div>
          <label htmlFor="email" className={labelCls}>Email *</label>
          <input
            id="email"
            type="email"
            data-testid="contact-input-email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            autoComplete="email"
            className={inputCls(errors.email)}
            placeholder="you@business.ca"
          />
          {errors.email && <p className={errorCls} role="alert">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="phone" className={labelCls}>Phone</label>
          <input
            id="phone"
            type="tel"
            data-testid="contact-input-phone"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            autoComplete="tel"
            className={inputCls()}
            placeholder="289-555-0100"
          />
        </div>
        <div>
          <label htmlFor="province" className={labelCls}>Province</label>
          <select
            id="province"
            data-testid="contact-input-province"
            value={form.province}
            onChange={(e) => set("province", e.target.value)}
            className={`${inputCls()} appearance-none pr-8`}
          >
            <option value="">Select a province</option>
            {PROVINCES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="employees" className={labelCls}>Number of Employees</label>
          <select
            id="employees"
            data-testid="contact-input-employees"
            value={form.employees}
            onChange={(e) => set("employees", e.target.value)}
            className={`${inputCls()} appearance-none pr-8`}
          >
            <option value="">Select a range</option>
            {EMPLOYEE_RANGES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="software" className={labelCls}>Current Accounting Software</label>
          <input
            id="software"
            data-testid="contact-input-software"
            value={form.software}
            onChange={(e) => set("software", e.target.value)}
            className={inputCls()}
            placeholder="e.g. QuickBooks Online, Sage, spreadsheets…"
          />
        </div>
      </div>

      <fieldset className="mt-7">
        <legend className={labelCls}>Services Needed *</legend>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Services needed">
          {SERVICE_OPTIONS.map((s) => {
            const active = form.services.includes(s);
            return (
              <button
                type="button"
                key={s}
                aria-pressed={active}
                data-testid={`contact-service-${slug(s)}`}
                onClick={() => toggleService(s)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "border-brand bg-brand text-white"
                    : "border-ink/15 bg-white text-slate-600 hover:border-brand hover:text-brand"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
        {errors.services && <p className={errorCls} role="alert">{errors.services}</p>}
      </fieldset>

      <div className="mt-7">
        <label htmlFor="message" className={labelCls}>Tell us about your business / What can we help with?</label>
        <textarea
          id="message"
          data-testid="contact-input-message"
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          rows={4}
          className="w-full rounded-xl border border-ink/10 bg-paper px-4 py-3 text-[15px] outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:bg-white"
          placeholder="A few lines about your business and what you need help with…"
        />
      </div>

      <div className="mt-6 flex items-start gap-3">
        <input
          id="consent"
          type="checkbox"
          data-testid="contact-consent-checkbox"
          checked={form.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-ink/20 accent-brand"
        />
        <label htmlFor="consent" className="text-sm leading-relaxed text-slate-600">
          I consent to being contacted regarding my inquiry. *
        </label>
      </div>
      {errors.consent && <p className={errorCls} role="alert">{errors.consent}</p>}

      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => set("website", e.target.value)}
        />
      </div>

      {status === "error" && (
        <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="contact-error">
          {serverError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        data-testid="contact-submit-button"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-3.5 text-sm font-bold text-white shadow-ambient transition-all duration-200 hover:-translate-y-px hover:bg-[rgb(0_40_140)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {status === "sending" ? "Sending…" : "Send My Inquiry"}
      </button>
      <p className="mt-4 text-xs text-slate-400">
        We&apos;ll only use your details to respond to this inquiry — never for promotional
        marketing.
      </p>
    </form>
  );
}
